/**
 * OtaLogo — zentraler Renderer für die hinterlegten OTA-Brandmarks.
 *
 * Zustaende:
 *   active=true  → gefuellt, volle Brand-Farbe
 *   active=false → derselbe Brandmark, gedimmt (gemappt aber noch nicht live)
 */
import airbnbLogo from '@/assets/ota/airbnb.svg';
import bookingComLogo from '@/assets/ota/booking-com.svg';
import { cn } from '@/shared/lib/cn';

// StayOn verteilt nur an Booking.com + Airbnb. Ein unbekannter Kanal-String faellt auf die
// graue "?"-Bubble zurueck (siehe OtaLogo-Fallback) — bricht also nichts, falls Channex mal
// einen anderen Kanal liefert.
export type OtaChannel = 'BookingCom' | 'Airbnb';

const CHANNEL_META: Record<OtaChannel, { label: string; src: string }> = {
  BookingCom: { label: 'Booking.com', src: bookingComLogo },
  Airbnb: { label: 'Airbnb', src: airbnbLogo },
};

/**
 * Channex hat keine deterministische Casing-Garantie ("Airbnb" vs "AirBNB" vs "airbnb"
 * je nach Erzeugungs-Pfad). Wir normalisieren auf unsere Brand-Keys.
 */
function normalizeChannel(raw: string): OtaChannel | string {
  const lower = (raw ?? '').toLowerCase().replace(/[.\s-]/g, '');
  switch (lower) {
    case 'airbnb':
    case 'airbnbcom':
      return 'Airbnb';
    case 'bookingcom':
    case 'booking':
      return 'BookingCom';
    default:
      return raw;
  }
}

interface OtaLogoProps {
  channel: string;
  active?: boolean;
  size?: number;
  title?: string;
}

export function OtaLogo({ channel, active = true, size = 20, title }: OtaLogoProps) {
  const normalized = normalizeChannel(channel);
  const meta = (CHANNEL_META as Record<string, (typeof CHANNEL_META)[OtaChannel]>)[normalized];
  if (meta) {
    return (
      <img
        src={meta.src}
        alt={title ?? meta.label}
        title={title ?? meta.label}
        width={size}
        height={size}
        className={cn('inline-block shrink-0 object-contain', !active && 'opacity-50')}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={title ?? `Sonstige OTA (${channel})`}
      title={title ?? `Sonstige OTA (${channel})`}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-serif font-semibold',
        active ? 'shadow-sm' : 'opacity-50',
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.5),
        background: active ? 'var(--color-ink-soft)' : 'transparent',
        color: active ? '#ffffff' : 'var(--color-ink-faint)',
        border: active ? 'none' : `1.5px solid var(--color-ink-faint)`,
      }}
    >
      ?
    </span>
  );
}

/** Unbekannte Kanäle heißen ehrlich „Sonstige OTA" statt eines rohen Vendor-Schlüssels (§11). */
export function getOtaLabel(channel: string): string {
  const normalized = normalizeChannel(channel);
  return (
    (CHANNEL_META as Record<string, (typeof CHANNEL_META)[OtaChannel]>)[normalized]?.label ??
    `Sonstige OTA (${channel})`
  );
}

export { normalizeChannel };
